// regionHelpers.js

import {
  kenyaRegions,
  kenyaDistricts,
  kenyaMunicipality,
  namibiaRegions,
  namibiaMunicipality,
  SouthAfricaRegions,
  SouthAfricaMunicipality,
  zambiaRegions,
  zambiaMunicipality,
  BurundiRegions,
  BurundiMunicipality,
  RwandaRegions,
  RwandaMunicipality,
  UgandaRegions,
  UgandaMunicipality,
  MalawiRegions,
  MalawiMunicipality,
  AngolaRegions,
  AngolaMunicipalities,
  dropdownItems,
  BurkinaFasoRegions,
  EswatiniRegions,
  LesothoRegions,
  TanzaniaRegions,
  BurkinaFasoMunicipality,
  EswatiniMunicipality,
  LesothoMunicipalities,
  TanzaniaMunicipalities,
  CameroonRegions,
  CameroonMunicipality,
  drcRegion,
  drcMunicipality,
  ethRegion,
  ethMunicipality,
  ivorianRegion,
  ivorianMunicipality
} from './regionData';
import { ghanaRegions, ghanaMunicipality } from './regionData';
import { mozambiqueRegions, mozambiqueMunicipality } from './regionData';
import { nigeriaRegions, nigeriaMunicipality } from './regionData';
import { senegalRegions, senegalMunicipality } from './regionData';

// Helper: Get country code from dropdownItems
export function getCountryCode(countryName) {
  const country = dropdownItems.COUNTRY.find(c => c.name === countryName);
  return country ? country.code : "254"; // Default to Kenya if not found
}

// Helper: Get region list based on country code
export function getRegionList(countryCode) {
  switch(countryCode) {
    case "254": return kenyaRegions; // Kenya
    case "264": return namibiaRegions; // Namibia
    case "27": return SouthAfricaRegions; // South Africa
    case "260": return zambiaRegions; // Zambia
    case "257": return BurundiRegions; // Burundi
    case "250": return RwandaRegions; // Rwanda
    case "256": return UgandaRegions; // Uganda
    case "265": return MalawiRegions; // Malawi
    case "244": return AngolaRegions; // Angola
    case "226": return BurkinaFasoRegions; // Burkina Faso
    case "268": return EswatiniRegions;    // Eswatini
    case "266": return LesothoRegions;     // Lesotho
    case "255": return TanzaniaRegions;    // Tanzania
    case "237": return CameroonRegions;    // Cameroon
    case "243": return drcRegion;          // DRC
    case "251": return ethRegion;          // Ethiopia
    case "225": return ivorianRegion;     // Côte d'Ivoire
    case "233": return ghanaRegions;      // Ghana (new addition)
    case "234": return nigeriaRegions; // Nigeria
    case "221": return senegalRegions; // Senegal
    case "258": return mozambiqueRegions; // Mozambique
    default: return dropdownItems.REGION; // fallback
  }
}

// Helper: Get district list based on country code and optional region value
export function getDistrictList(countryCode, regionValue) {
  switch(countryCode) {
    case "254": return kenyaMunicipality; // Kenya
    case "264": return namibiaMunicipality; // Namibia
    case "27": { // South Africa - filter by region if provided
      if (!regionValue) return SouthAfricaMunicipality;
      
      // Extract numeric part from region value (handles both "01" and "(01)" formats)
      const regionNum = regionValue.toString().replace(/\D/g, '');
      
      // Define district ranges for each region
      const regionDistricts = {
        '01': [1, 12],    // Gauteng (001-012)
        '02': [13, 60],   // Eastern Cape (013-060)
        '03': [61, 90],   // Limpopo (061-090)
        '04': [91, 152],  // KwaZulu-Natal (091-152)
        '05': [153, 173], // Mpumalanga (153-173)
        '06': [174, 203], // Western Cape (174-203)
        '07': [204, 226], // North West (204-226)
        '08': [227, 258], // Northern Cape (227-258)
        '09': [259, 284]  // Free State (259-284)
      };
      
      const [min, max] = regionDistricts[regionNum] || [0, Infinity];
      
      return SouthAfricaMunicipality.filter(municipality => {
        // Extract numeric part from municipality value
        const munValue = parseInt(municipality.value.replace(/\D/g, ''));
        return munValue >= min && munValue <= max;
      });
    }
    case "260": return zambiaMunicipality; // Zambia
    case "257": return BurundiMunicipality; // Burundi
    case "250": return RwandaMunicipality; // Rwanda
    case "256": return UgandaMunicipality; // Uganda
    case "265": return MalawiMunicipality; // Malawi
    case "244": return AngolaMunicipalities; // Angola
    case "226": return BurkinaFasoMunicipality;
    case "268": return EswatiniMunicipality;
    case "266": return LesothoMunicipalities;
    case "255": return TanzaniaMunicipalities;
    case "237": return CameroonMunicipality; // Cameroon
    case "243": return drcMunicipality;      // DRC
    case "251": return ethMunicipality;      // Ethiopia
    case "225": { // Côte d'Ivoire - filter by region if provided
      if (!regionValue) return ivorianMunicipality;
      
      const regionNum = regionValue.toString().replace(/\D/g, '');
      return ivorianMunicipality.filter(municipality => 
        municipality.regionCode.toString() === regionNum
      );
    }
    case "233": return ghanaMunicipality;   // Ghana (new addition)
    case "258": { // Mozambique - filter by region if provided
      if (!regionValue) return mozambiqueMunicipality;
      
      const regionNum = parseInt(regionValue);
      return mozambiqueMunicipality.filter(municipality => 
        municipality.provinceCode === regionNum
      );
    }
    case "234": { // Nigeria - filter by region if provided
      if (!regionValue) return nigeriaMunicipality;
      
      const regionNum = parseInt(regionValue);
      return nigeriaMunicipality.filter(municipality => 
        municipality.zoneCode === regionNum
      );
    }
    case "221": { // Senegal - filter by region if provided
      if (!regionValue) return senegalMunicipality;
      
      const regionNum = regionValue.toString().replace(/\D/g, '');
      return senegalMunicipality.filter(municipality => 
        municipality.regionCode.toString() === regionNum
      );
    }
    default: return dropdownItems.DISTRICT; // fallback
  }
}

// Helper: Get municipality list based on country code
export function getMunicipalityList(countryCode) {
  // For most countries, municipality is the same as district
  return getDistrictList(countryCode);
}